import { useEffect, useState, useCallback } from "react";
import classNames from "classnames";

import styles from "./Slider.module.css";

type PropTypes = {
  title: string;
  slides: React.ReactNode[];
  autoScrollInterval: number | null;
};

let scrollInterval: NodeJS.Timer | null = null;

// 100%: slide width
// 10%: gap between slides
const SLIDE_WIDTH = 110;

const Slider: React.FC<PropTypes> = ({ title, slides, autoScrollInterval = null }) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const stopAutoScroll = useCallback(() => {
    if (scrollInterval !== null) {
      clearInterval(scrollInterval);
    }
  }, []);

  const restartAutoScroll = useCallback(() => {
    stopAutoScroll();
    if (autoScrollInterval !== null) {
      scrollInterval = setInterval(() => {
        setCurrentSlide((currentSlide) => (currentSlide + 1) % slides.length);
      }, autoScrollInterval);
    }
  }, [stopAutoScroll, autoScrollInterval, setCurrentSlide, slides.length]);

  useEffect(() => {
    restartAutoScroll();
    return stopAutoScroll;
  }, [restartAutoScroll, stopAutoScroll]);

  const onChangeSlide = useCallback(
    (slide: number) => {
      setCurrentSlide(slide);
      restartAutoScroll();
    },
    [setCurrentSlide, restartAutoScroll]
  );

  return (
    <>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.wrapper} onMouseOver={stopAutoScroll} onMouseOut={restartAutoScroll}>
        {slides.map((slide, index) => (
          <div
            className={styles.slide}
            key={index}
            style={{ marginLeft: index === 0 ? currentSlide * SLIDE_WIDTH * -1 + "%" : 0 }}
          >
            {slide}
          </div>
        ))}
      </div>
      <div className={styles.navigation}>
        {slides.map((_slide, index) => (
          <div
            key={index}
            onClick={() => onChangeSlide(index)}
            className={classNames(styles.dot, { [styles.active]: index === currentSlide })}
          ></div>
        ))}
      </div>
    </>
  );
};

export default Slider;
